import React, { useState, useEffect } from 'react';
import { calculatePlantCount } from '../algorithms/plantCalculations';
import theme from '../theme';
import { validateAndSanitizeText } from '../utils/sanitization';

function PlantGroupModal({ show, onClose, onSave, cropAreas, editPlantGroup = null }) {
  const [slotId, setSlotId] = useState('');
  const [plantName, setPlantName] = useState('');
  const [description, setDescription] = useState('');
  const [rowSpacing, setRowSpacing] = useState(1.0);
  const [colSpacing, setColSpacing] = useState(1.0);
  const [rowMargin, setRowMargin] = useState(0);
  const [colMargin, setColMargin] = useState(0);
  const [numRows, setNumRows] = useState(0);
  const [numCols, setNumCols] = useState(0);

  useEffect(() => {
    if (editPlantGroup) {
      setSlotId(editPlantGroup.slotId || '');
      setPlantName(editPlantGroup.plantName || '');
      setDescription(editPlantGroup.description || '');
      setRowSpacing(editPlantGroup.rowSpacing || 1.0);
      setColSpacing(editPlantGroup.colSpacing || 1.0);
      // Support both old "offset" and new "margin" field names for backward compatibility
      setRowMargin(editPlantGroup.rowMargin ?? editPlantGroup.rowOffset ?? 0);
      setColMargin(editPlantGroup.colMargin ?? editPlantGroup.colOffset ?? 0);
      setNumRows(editPlantGroup.numRows || 0);
      setNumCols(editPlantGroup.numCols || 0);
    } else {
      // Reset when not editing
      setSlotId('');
      setPlantName('');
      setDescription('');
      setRowSpacing(1.0);
      setColSpacing(1.0);
      setRowMargin(0);
      setColMargin(0);
      setNumRows(0);
      setNumCols(0);
    }
  }, [editPlantGroup, show]);

  if (!show) return null;

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!slotId || !plantName.trim()) {
      alert('Please select a slot and enter a plant name');
      return;
    }

    const selectedSlot = cropAreas.find(area => area.id === slotId);
    if (!selectedSlot) {
      alert('Selected slot not found');
      return;
    }

    const plantCount = calculatePlantCount(selectedSlot, rowSpacing, colSpacing, rowMargin, colMargin, numRows, numCols);

    // Sanitize user inputs before saving
    const plantGroup = {
      id: editPlantGroup?.id || Date.now().toString(),
      slotId,
      slotName: selectedSlot.name,
      plantName: validateAndSanitizeText(plantName.trim(), 200),
      description: validateAndSanitizeText(description.trim(), 1000),
      rowSpacing,
      colSpacing,
      rowMargin,
      colMargin,
      numRows,
      numCols,
      actualRows: plantCount.rows,
      actualCols: plantCount.cols,
      totalPlants: plantCount.total,
      createdAt: editPlantGroup?.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    onSave(plantGroup);
    onClose();
  };

  const selectedSlot = slotId ? cropAreas.find(area => area.id === slotId) : null;
  const plantCount = selectedSlot ? calculatePlantCount(selectedSlot, rowSpacing, colSpacing, rowMargin, colMargin, numRows, numCols) : { rows: 0, cols: 0, total: 0 };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'white',
        padding: '20px',
        borderRadius: '8px',
        maxWidth: '600px',
        width: '90%',
        maxHeight: '90vh',
        overflowY: 'auto'
      }}>
        <h2>{editPlantGroup ? 'Edit Plant Group' : 'Add Plant Group'}</h2>
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '15px' }}>
            <label htmlFor="slotId" style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              Select Slot (Crop Area):
            </label>
            <select
              id="slotId"
              value={slotId}
              onChange={(e) => setSlotId(e.target.value)}
              style={{ width: '100%', padding: '8px', fontSize: '14px' }}
              required
            >
              <option value="">-- Select a crop area --</option>
              {cropAreas.map(area => (
                <option key={area.id} value={area.id}>
                  {area.name} ({area.width.toFixed(1)}m × {area.length.toFixed(1)}m)
                </option>
              ))}
            </select>
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label htmlFor="plantName" style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              Plant Name:
            </label>
            <input
              id="plantName"
              type="text"
              value={plantName}
              onChange={(e) => setPlantName(e.target.value)}
              placeholder="e.g., Tomatoes, Corn, etc."
              style={{ width: '100%', padding: '8px', fontSize: '14px' }}
              required
            />
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label htmlFor="description" style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              Description:
            </label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Enter description or notes..."
              style={{ width: '100%', padding: '8px', fontSize: '14px', minHeight: '60px' }}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '15px' }}>
            <div>
              <label htmlFor="rowSpacing" style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                Row Spacing (m):
              </label>
              <input
                id="rowSpacing"
                type="number"
                value={rowSpacing}
                onChange={(e) => setRowSpacing(parseFloat(e.target.value) || 0.1)}
                min="0.1"
                step="0.1"
                style={{ width: '100%', padding: '8px', fontSize: '14px' }}
                required
              />
              <small style={{ color: '#666', fontSize: '12px' }}>
                Along length ({selectedSlot ? `${selectedSlot.length.toFixed(1)}m` : '—'})
              </small>
            </div>
            <div>
              <label htmlFor="colSpacing" style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                Column Spacing (m):
              </label>
              <input
                id="colSpacing"
                type="number"
                value={colSpacing}
                onChange={(e) => setColSpacing(parseFloat(e.target.value) || 0.1)}
                min="0.1"
                step="0.1"
                style={{ width: '100%', padding: '8px', fontSize: '14px' }}
                required
              />
              <small style={{ color: '#666', fontSize: '12px' }}>
                Along width ({selectedSlot ? `${selectedSlot.width.toFixed(1)}m` : '—'})
              </small>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '15px' }}>
            <div>
              <label htmlFor="rowMargin" style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                Row Margin (m):
              </label>
              <input
                id="rowMargin"
                type="number"
                value={rowMargin}
                onChange={(e) => setRowMargin(parseFloat(e.target.value) || 0)}
                min="0"
                step="0.01"
                style={{ width: '100%', padding: '8px', fontSize: '14px' }}
              />
              <small style={{ color: '#666', fontSize: '12px' }}>Margin from both length edges</small>
            </div>
            <div>
              <label htmlFor="colMargin" style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                Column Margin (m):
              </label>
              <input
                id="colMargin"
                type="number"
                value={colMargin}
                onChange={(e) => setColMargin(parseFloat(e.target.value) || 0)}
                min="0"
                step="0.01"
                style={{ width: '100%', padding: '8px', fontSize: '14px' }}
              />
              <small style={{ color: '#666', fontSize: '12px' }}>Margin from both width edges</small>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '15px' }}>
            <div>
              <label htmlFor="numRows" style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                Number of Rows (0 = auto):
              </label>
              <input
                id="numRows"
                type="number"
                value={numRows}
                onChange={(e) => setNumRows(parseInt(e.target.value) || 0)}
                min="0"
                step="1"
                style={{ width: '100%', padding: '8px', fontSize: '14px' }}
              />
            </div>
            <div>
              <label htmlFor="numCols" style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                Number of Columns (0 = auto):
              </label>
              <input
                id="numCols"
                type="number"
                value={numCols}
                onChange={(e) => setNumCols(parseInt(e.target.value) || 0)}
                min="0"
                step="1"
                style={{ width: '100%', padding: '8px', fontSize: '14px' }}
              />
            </div>
          </div>

          {selectedSlot && (
            <div style={{
              backgroundColor: theme.colors.primaryPale,
              padding: '10px',
              borderRadius: '5px',
              marginBottom: '15px',
              border: `1px solid ${theme.colors.primary}`
            }}>
              <strong>Calculated Plant Count:</strong>
              <div style={{ marginTop: '5px' }}>
                Rows: {plantCount.rows} | Columns: {plantCount.cols}
              </div>
              <div style={{ fontSize: '18px', fontWeight: 'bold', marginTop: '5px' }}>
                Total Plants: {plantCount.total}
              </div>
            </div>
          )}

          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: '10px 20px',
                backgroundColor: theme.colors.mediumGrey,
                color: 'white',
                border: 'none',
                borderRadius: '5px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              style={{
                padding: '10px 20px',
                backgroundColor: theme.colors.success,
                color: 'white',
                border: 'none',
                borderRadius: '5px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              {editPlantGroup ? 'Update' : 'Create'} Plant Group
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default PlantGroupModal;
